import assetStatusService from "../../../../services/assetService/assetStatusService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_ASSETSTATUSES } from "../../../../utils/constants";
import { AssetStatusInterface } from "../../../../models/AssetModel/AssetStatus";

interface UpdateAssetStatusesContext {
  previousAssetStatuses: AssetStatusInterface[];
}

const useUpdateAssetStatus = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    AssetStatusInterface,
    Error,
    AssetStatusInterface,
    UpdateAssetStatusesContext
  >({
    mutationFn: async (assetStatus: AssetStatusInterface) => {
      // Assuming the ID is stored in the assetStatus object or can be derived
      const { id, ...assetStatusData } = assetStatus;
      return assetStatusService.Update(id || "", assetStatusData);
    },

    onMutate: async (updatedAssetStatus: AssetStatusInterface) => {
      await queryClient.cancelQueries({ queryKey: [CACHE_KEY_ASSETSTATUSES] });

      const previousAssetStatuses =
        queryClient.getQueryData<AssetStatusInterface[]>([
          CACHE_KEY_ASSETSTATUSES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ]) || [];

      queryClient.setQueryData<AssetStatusInterface[]>(
        [
          CACHE_KEY_ASSETSTATUSES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (assetStatuses = []) =>
          assetStatuses.map((assetStatus) =>
            assetStatus.id === updatedAssetStatus.id
              ? updatedAssetStatus
              : assetStatus
          )
      );

      return { previousAssetStatuses };
    },

    onSuccess: (savedAssetStatus) => {
      queryClient.setQueryData<AssetStatusInterface[]>(
        [
          CACHE_KEY_ASSETSTATUSES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (assetStatuses) =>
          assetStatuses?.map((assetStatus) =>
            assetStatus.id === savedAssetStatus.id
              ? savedAssetStatus
              : assetStatus
          )
      );
    },

    onError: (error, updatedAssetStatus, context) => {
      if (!context) return;

      queryClient.setQueryData<AssetStatusInterface[]>(
        [
          CACHE_KEY_ASSETSTATUSES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousAssetStatuses
      );
    },
  });
};

export default useUpdateAssetStatus;
